import { motion } from "framer-motion";
import {
  CheckCircle2, XCircle, TrendingUp, Scissors, Stethoscope, UtensilsCrossed,
  Home, Dumbbell, ShoppingBag, Briefcase, Car, Hotel, Sparkles,
} from "lucide-react";
import { PLATFORMS } from "@/data/platforms";
import { Reveal } from "@/components/anim/Reveal";
import Tilt3D from "@/components/anim/Tilt3D";
import { HE } from "@/lib/i18n";
import { t } from "@/lib/strings";

const CARDS_EN = [
  { status: "Mentioned", ok: true, left: "3%", top: "56%" },
  { status: "Cited #2", ok: true, left: "19%", top: "24%" },
  { status: "Not cited", ok: false, left: "35%", top: "60%" },
  { status: "Mentioned", ok: true, left: "51%", top: "20%" },
  { status: "Not cited", ok: false, left: "64%", top: "56%" },
  { status: "Cited #1", ok: true, left: "78%", top: "14%" },
];

const CARDS_HE = [
  { status: "מוזכר", ok: true, left: "3%", top: "56%" },
  { status: "מצוטט #2", ok: true, left: "19%", top: "24%" },
  { status: "לא מצוטט", ok: false, left: "35%", top: "60%" },
  { status: "מוזכר", ok: true, left: "51%", top: "20%" },
  { status: "לא מצוטט", ok: false, left: "64%", top: "56%" },
  { status: "מצוטט #1", ok: true, left: "78%", top: "14%" },
];

const CARDS = HE ? CARDS_HE : CARDS_EN;

const SPARKLES = [
  { left: "12%", top: "12%", d: 0 },
  { left: "45%", top: "44%", d: 0.8 },
  { left: "70%", top: "30%", d: 1.5 },
  { left: "90%", top: "10%", d: 0.4 },
  { left: "28%", top: "80%", d: 1.1 },
];

const INDUSTRIES_EN = [
  [Scissors, "Barbershops & Salons"],
  [Stethoscope, "Clinics & Dentists"],
  [UtensilsCrossed, "Restaurants & Cafés"],
  [Home, "Real Estate"],
  [Dumbbell, "Gyms & Studios"],
  [ShoppingBag, "E-commerce & Retail"],
  [Briefcase, "Law, Finance & Agencies"],
  [Car, "Auto Services"],
  [Hotel, "Hotels & Travel"],
];

const INDUSTRIES_HE = [
  [Scissors, "מספרות וסלונים"],
  [Stethoscope, "מרפאות ורופאי שיניים"],
  [UtensilsCrossed, "מסעדות ובתי קפה"],
  [Home, "נדל\"ן"],
  [Dumbbell, "חדרי כושר וסטודיו"],
  [ShoppingBag, "איקומרס וקמעונאות"],
  [Briefcase, "עריכת דין, פיננסים וסוכנויות"],
  [Car, "מוסכים ושירותי רכב"],
  [Hotel, "מלונות ותיירות"],
];

const INDUSTRIES = HE ? INDUSTRIES_HE : INDUSTRIES_EN;

const StatusLine = ({ ok, status }) => (
  <div className={`flex items-center gap-1 font-data text-[10px] font-bold uppercase tracking-wide ${ok ? "text-data-positive" : "text-data-negative"}`}>
    {ok ? <CheckCircle2 className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}
    {status}
  </div>
);

const FlowCard = ({ p, card, i, floating = true }) => {
  const Icon = p.Icon;
  const inner = (
    <div className="flex items-center gap-2.5 bg-white border-2 border-obsidian px-3 py-2 sm:px-3.5 sm:py-2.5 shadow-[5px_5px_0px_0px_rgba(10,10,10,0.15)]">
      <Icon style={{ fontSize: 18, color: p.color, flexShrink: 0 }} />
      <div>
        <div className="font-heading font-bold text-[13px] text-obsidian leading-tight whitespace-nowrap">{p.short || p.name}</div>
        <StatusLine ok={card.ok} status={card.status} />
      </div>
    </div>
  );
  if (!floating) return inner;
  return (
    <motion.div
      className="absolute z-10"
      style={{ left: card.left, top: card.top }}
      initial={{ opacity: 0, y: 26 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ delay: 0.35 + i * 0.14, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
    >
      <motion.div animate={{ y: [0, -8, 0] }} transition={{ repeat: Infinity, duration: 3.2 + i * 0.4, ease: "easeInOut" }}>
        <Tilt3D max={12}>{inner}</Tilt3D>
      </motion.div>
    </motion.div>
  );
};

const ScoreCard = ({ floating = true }) => {
  const inner = (
    <div className="bg-obsidian text-white border-2 border-obsidian px-4 py-3 shadow-[6px_6px_0px_0px_rgba(255,69,0,1)]">
      <div className="font-data text-[9px] uppercase tracking-[0.2em] text-neutral-400">{t("flow.score")}</div>
      <div className="flex items-end gap-2 mt-0.5">
        <span className="font-display text-3xl leading-none">87</span>
        <span className="flex items-center gap-1 font-data text-[10px] font-bold text-data-positive pb-0.5">
          <TrendingUp className="h-3 w-3" /> +12
        </span>
      </div>
    </div>
  );
  if (!floating) return inner;
  return (
    <motion.div
      className="absolute z-10"
      style={{ left: "81%", top: "52%" }}
      initial={{ opacity: 0, scale: 0.85 }}
      whileInView={{ opacity: 1, scale: 1 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ delay: 1.3, type: "spring", stiffness: 260, damping: 20 }}
    >
      <motion.div animate={{ y: [0, -9, 0] }} transition={{ repeat: Infinity, duration: 3.6, ease: "easeInOut" }}>
        {inner}
      </motion.div>
    </motion.div>
  );
};

export const VisibilityFlow = () => (
  <section className="py-20 md:py-28 border-b-2 border-obsidian bg-white overflow-hidden">
    <div className="av-container">
      <Reveal className="max-w-3xl">
        <div className="flex items-center gap-3 mb-4">
          <div className="h-3 w-3 bg-conversion" />
          <span className="av-label text-obsidian">{t("flow.label")}</span>
        </div>
        {HE ? (
          <>
            <h2 className="font-display text-3xl sm:text-5xl leading-[1.05] text-obsidian">
              אם לקוחות שואלים על זה את ה-AI — <span className="text-conversion">אנחנו בודקים את זה.</span>
            </h2>
            <p className="mt-5 text-lg md:text-xl text-neutral-700 leading-relaxed">
              מיליוני אנשים שואלים את ה-AI על מי לסמוך, כל יום. סריקה אחת מראה בדיוק איפה ממליצים
              על העסק שלכם, איפה אתם שקופים — ואיך הופכים את זה.
            </p>
          </>
        ) : (
          <>
            <h2 className="font-display text-3xl sm:text-5xl leading-[1.05] text-obsidian">
              If customers ask AI about it — <span className="text-conversion">we track it.</span>
            </h2>
            <p className="mt-5 text-lg md:text-xl text-neutral-700 leading-relaxed">
              Millions of people ask AI who to trust every single day. One scan shows you exactly where
              your business is recommended, where you&apos;re invisible — and how to flip it.
            </p>
          </>
        )}
      </Reveal>

      {/* Desktop: floating flow scene */}
      <div className="hidden md:block relative mt-14 h-[380px]" data-testid="visibility-flow">
        {/* faint grid + animated spline */}
        <svg viewBox="0 0 1200 380" preserveAspectRatio="none" className="absolute inset-0 w-full h-full">
          {[76, 152, 228, 304].map((y) => (
            <line key={y} x1="0" y1={y} x2="1200" y2={y} stroke="rgba(10,10,10,0.06)" strokeWidth="1" />
          ))}
          <motion.path
            d="M 0 300 C 130 190, 210 330, 350 260 C 450 210, 500 120, 610 160 C 700 195, 730 300, 840 260 C 940 225, 990 100, 1090 90 C 1130 86, 1170 70, 1200 55"
            fill="none"
            stroke="#FF4500"
            strokeWidth="3.5"
            initial={{ pathLength: 0 }}
            whileInView={{ pathLength: 1 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 2.2, ease: [0.22, 1, 0.36, 1] }}
          />
          <motion.path
            d="M 0 330 C 150 300, 260 350, 400 320 C 540 290, 640 340, 780 330 C 920 320, 1040 340, 1200 335"
            fill="none"
            stroke="rgba(10,10,10,0.18)"
            strokeWidth="2"
            strokeDasharray="6 6"
            initial={{ pathLength: 0 }}
            whileInView={{ pathLength: 1 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 2.2, delay: 0.3 }}
          />
        </svg>
        <span className="absolute left-1 bottom-6 font-data text-[9px] uppercase tracking-widest text-neutral-400">{t("flow.legendComp")}</span>
        <span className="absolute left-1 top-2 font-data text-[9px] uppercase tracking-widest text-conversion">{t("flow.legendYou")}</span>

        {SPARKLES.map((s) => (
          <motion.span
            key={`${s.left}-${s.top}`}
            className="absolute z-0 text-conversion/60"
            style={{ left: s.left, top: s.top }}
            animate={{ opacity: [0.15, 0.9, 0.15], scale: [0.7, 1.15, 0.7], rotate: [0, 45, 0] }}
            transition={{ repeat: Infinity, duration: 3.4, delay: s.d }}
          >
            <Sparkles className="h-4 w-4" />
          </motion.span>
        ))}

        {PLATFORMS.map((p, i) => (
          <FlowCard key={p.name} p={p} card={CARDS[i]} i={i} />
        ))}
        <ScoreCard />
      </div>

      {/* Mobile: compact grid version */}
      <div className="md:hidden mt-10 space-y-3" data-testid="visibility-flow-mobile">
        <div className="grid grid-cols-2 gap-3">
          {PLATFORMS.map((p, i) => (
            <motion.div
              key={p.name}
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ delay: i * 0.08 }}
            >
              <FlowCard p={p} card={CARDS[i]} i={i} floating={false} />
            </motion.div>
          ))}
        </div>
        <motion.div initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.5 }}>
          <ScoreCard floating={false} />
        </motion.div>
      </div>
    </div>

    {/* industries marquee */}
    <div className="mt-16 border-y-2 border-obsidian bg-[#F9F9F9] py-4 overflow-hidden">
      <div className="flex w-max av-marquee gap-4 px-4">
        {[...INDUSTRIES, ...INDUSTRIES].map(([Icon, label], i) => (
          <span key={`${label}-${i}`} className="flex items-center gap-2 border-2 border-obsidian bg-white px-4 py-2 whitespace-nowrap">
            <Icon className="h-4 w-4 text-conversion" strokeWidth={2} />
            <span className="font-data text-xs font-bold uppercase tracking-wide text-obsidian">{label}</span>
          </span>
        ))}
      </div>
      <p className="text-center mt-4 font-data text-[11px] uppercase tracking-[0.25em] text-neutral-500 px-6">
        {t("flow.marquee")}
      </p>
    </div>
  </section>
);

export default VisibilityFlow;
